// src/components/TransactionReceipt.js
import React, { useState } from 'react';

export default function TransactionReceipt({ transaction, onClose }) {
  const [docView, setDocView] = useState('payout');
  if (!transaction) return null;

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format a bare calendar date (YYYY-MM-DD) with NO timezone conversion.
  // A due date is a calendar day, not an instant — never parse via new Date().
  const formatDueDate = (date) => {
    if (!date) return 'N/A';
    const ymd = String(date).slice(0, 10);           // "2026-07-14"
    const [y, m, d] = ymd.split('-').map(Number);
    if (!y || !m || !d) return 'N/A';
    const months = ['January','February','March','April','May','June',
                    'July','August','September','October','November','December'];
    return `${months[m - 1]} ${d}, ${y}`;            // "July 14, 2026"
  };

  const getDeliveryMethodLabel = (method) => {
    const methods = {
      'MPESA': 'M-Pesa Mobile Money',
      'BANK': 'Bank Transfer',
      'AIRTIME': 'Airtime Top-up',
    };
    return methods[method] || method || 'Mobile Money';
  };

  const getStatusColor = (status) => {
    const colors = {
      'completed': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'failed': 'bg-red-100 text-red-800',
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const settlement = transaction.snplSettlement || {};
  const isSNPL = transaction.transactionType === 'SNPL';
  const reference = transaction.reference || (transaction.transactionType === 'SNPL' ? 'SNPL-' : transaction.transactionType === 'SNPN' ? 'SNPN-' : 'TXN-') + (transaction.id?.replace(/-/g,'').substring(0,6).toUpperCase() || 'N/A');

  const fxFee = parseFloat(transaction.variableFee || transaction.totalFee || 0);
  const amountConverted = Math.max(0, parseFloat(transaction.senderAmount || 0) - fxFee);
  const principal = parseFloat(transaction.senderAmount || 0);
  const extensionFees = parseFloat(settlement.extensionFeesTotal || 0);
  const totalDue = parseFloat(settlement.totalAmount || 0);
  const serviceFee = isSNPL
    ? Math.max(0, totalDue - principal - extensionFees).toFixed(2)
    : "0.00";
  const outstanding = parseFloat(settlement.remainingAmount || totalDue || 0);
  const repaymentPaid = settlement.status === 'paid' || outstanding <= 0;
  const repaymentStatus = repaymentPaid ? 'PAID' : 'PAYMENT PENDING';
  const documentTitle = !isSNPL
    ? 'Payout Receipt'
    : docView === 'invoice'
      ? 'Deferred Payment Invoice'
      : docView === 'payment'
        ? 'Deferred Payment Receipt'
        : 'Payout Receipt';

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-blue-600 text-white p-6 print:bg-white print:text-black">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-1">
                {documentTitle}
              </h2>
              <p className="text-blue-100 print:text-gray-600">
                Reference: {reference}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 text-2xl font-bold print:hidden"
            >
              ×
            </button>
          </div>
        </div>

        {/* Receipt Content */}
        <div className="p-6 space-y-6">
          {/* Document Tabs */}
          {isSNPL && (
            <div className="flex flex-wrap justify-center gap-2 print:hidden">
              <button
                onClick={() => setDocView('payout')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border ${docView === 'payout' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200'}`}
              >
                🧾 Payout Receipt
              </button>

              <button
                onClick={() => setDocView('invoice')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border ${docView === 'invoice' ? 'bg-yellow-500 text-white border-yellow-500' : 'bg-white text-yellow-700 border-yellow-200'}`}
              >
                📄 Deferred Invoice
              </button>

              <button
                onClick={() => repaymentPaid && setDocView('payment')}
                disabled={!repaymentPaid}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border ${docView === 'payment' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-green-700 border-green-200'} disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                ✅ Payment Receipt
              </button>
            </div>
          )}
          {/* Status Badges */}
          <div className="flex flex-wrap justify-center gap-2">
            <span className="px-4 py-2 rounded-full text-sm font-semibold bg-green-100 text-green-800">
              Payout Delivered
            </span>
            {isSNPL && docView !== 'payout' && (
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${repaymentPaid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                Deferred Payment {repaymentStatus}
              </span>
            )}
          </div>
          {/* SNPL Deferred Payment Invoice / Payment Receipt */}
          {isSNPL && docView !== 'payout' && (
            <div className={`${repaymentPaid ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-4`}>
              <p className={`font-bold mb-2 ${repaymentPaid ? 'text-green-900' : 'text-yellow-900'}`}>
                {repaymentPaid ? '✅ Deferred Payment Receipt' : '📄 Deferred Payment Invoice'}
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-gray-600">Invoice status</p>
                  <p className="font-bold">{repaymentStatus}</p>
                </div>
                <div>
                  <p className="text-gray-600">Outstanding balance</p>
                  <p className={`font-bold ${repaymentPaid ? 'text-green-700' : 'text-red-700'}`}>
                    ${outstanding.toFixed(2)} {transaction.senderCurrency}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Total amount due</p>
                  <p className="font-semibold">${totalDue.toFixed(2)} {transaction.senderCurrency}</p>
                </div>
                <div>
                  <p className="text-gray-600">Payment due by</p>
                  <p className="font-bold text-red-600">{formatDueDate(settlement.dueDate || transaction.dueDate)}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Recipient payout is delivered. This invoice records your separate deferred payment obligation to ZoomPay.
              </p>
            </div>
          )}
          {/* Transaction Details */}
          <div className="border-t border-b py-4 space-y-3">
            <h3 className="font-bold text-lg mb-3">Transaction Details</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Date & Time</p>
                <p className="font-semibold">{formatDate(transaction.createdAt)}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600">Type</p>
                <p className="font-semibold">{transaction.transactionType || 'SNPN'}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600">Delivery Method</p>
                <p className="font-semibold">{getDeliveryMethodLabel(transaction.payoutMethod)}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600">Corridor</p>
                <p className="font-semibold">{transaction.corridor || 'CA-KE'}</p>
              </div>
            </div>
          </div>

          {/* Amount Details */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <h3 className="font-bold text-lg mb-3">Amount Details</h3>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">You Sent</span>
                <span className="font-semibold">
                  {transaction.senderAmount} {transaction.senderCurrency || 'CAD'}
                </span>
              </div>

              {transaction.transactionType === 'SNPL' && docView !== 'payout' ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Exchange Fee</span>
                    <span className="font-semibold">{fxFee.toFixed(2)} {transaction.senderCurrency || 'CAD'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount Converted</span>
                    <span className="font-semibold">{amountConverted.toFixed(2)} {transaction.senderCurrency || 'CAD'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">SNPL Service Fee</span>
                    <span className="font-semibold">{serviceFee} {transaction.senderCurrency || 'CAD'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Extension Fees</span>
                    <span className="font-semibold">{extensionFees.toFixed(2)} {transaction.senderCurrency || 'CAD'}</span>
                  </div>
                  <div className="flex justify-between font-bold">
                    <span className="text-gray-800">Total Deferred Payment Due</span>
                    <span className="text-blue-700">{totalDue.toFixed(2)} {transaction.senderCurrency || 'CAD'}</span>
                  </div>
                  <div className="flex justify-between font-bold">
                    <span className="text-gray-800">Outstanding Balance</span>
                    <span className={repaymentPaid ? "text-green-700" : "text-red-700"}>{outstanding.toFixed(2)} {transaction.senderCurrency || 'CAD'}</span>
                  </div>
                  <div className="flex justify-between font-bold">
                    <span className="text-gray-800">Payment Due Date</span>
                    <span className="text-red-700">{formatDueDate(settlement.dueDate || transaction.dueDate)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">FX Fee (Included)</span>
                    <span className="font-semibold">{fxFee.toFixed(2)} {transaction.senderCurrency || 'CAD'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount Converted</span>
                    <span className="font-semibold">{amountConverted.toFixed(2)} {transaction.senderCurrency || 'CAD'}</span>
                  </div>
                </>
              )}

              <div className="flex justify-between">
                <span className="text-gray-600">Exchange Rate</span>
                <span className="font-semibold">
                  1 {transaction.senderCurrency || 'CAD'} = {transaction.exchangeRate || 0} {transaction.recipientCurrency || 'KES'}
                </span>
              </div>

              <div className="flex justify-between pt-2 border-t-2 border-gray-300">
                <span className="font-bold">Recipient Received</span>
                <span className="font-bold text-green-600 text-lg">
                  {transaction.recipientAmount} {transaction.recipientCurrency || 'KES'}
                </span>
              </div>
            </div>
          </div>

          {/* Recipient Details */}
          <div className="space-y-3">
            <h3 className="font-bold text-lg">Recipient Details</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-semibold">{transaction.recipientName || 'N/A'}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-semibold">{transaction.recipientPhone || 'N/A'}</p>
              </div>

              <div>
                <p className="text-sm text-gray-600">Country</p>
                <p className="font-semibold">{transaction.recipientCountry || 'Kenya'}</p>
              </div>

              {transaction.payoutMethod === 'BANK' && transaction.recipientBankAccount && (
                <div>
                  <p className="text-sm text-gray-600">Bank Account</p>
                  <p className="font-semibold">{transaction.recipientBankAccount}</p>
                </div>
              )}
            </div>
          </div>

          {/* Provider Info */}
          {transaction.payoutProvider && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Payment Provider</p>
              <p className="font-semibold">{transaction.payoutProvider}</p>
              {transaction.payoutExternalId && (
                <p className="text-xs text-gray-500 mt-1">
                  External ID: {transaction.payoutExternalId}
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 print:hidden">
            <button
              onClick={handlePrint}
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition"
            >
              🖨️ Print Receipt
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-semibold hover:bg-gray-300 transition"
            >
              Close
            </button>
          </div>

          {/* Footer */}
          <div className="text-center text-sm text-gray-500 pt-4 border-t">
            <p>Thank you for using ZoomPay</p>
            <p className="text-xs mt-1">
              For support: support@zoompay.co.ke | Quote your reference number
            </p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .fixed, .fixed * {
            visibility: visible;
          }
          .fixed {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}